/**
 * Linguagem do contexto catalog no front.
 *
 * As regras aqui são espelhos das do agregado `Course` (catalog/domain).
 * Servem para a interface avisar ANTES do clique — a API continua sendo
 * quem decide e recusa.
 */
import type { components } from '@/core/http';

export type CourseSummary = components['schemas']['CourseSummaryOut'];
export type Course = components['schemas']['CourseOut'];
export type CourseModule = components['schemas']['ModuleOut'];
export type Lesson = components['schemas']['LessonOut'];

/** Espelha `ContentStatus`. */
export const CourseStatus = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
} as const;

/** Espelha `LessonKind`. */
export const LessonKind = {
  VIDEO: 'video',
  TEXT: 'text',
  QUIZ: 'quiz',
  LIVE: 'live',
} as const;
export type LessonKind = (typeof LessonKind)[keyof typeof LessonKind];

export const STATUS_LABELS: Record<string, string> = {
  [CourseStatus.DRAFT]: 'Rascunho',
  [CourseStatus.PUBLISHED]: 'Publicado',
  [CourseStatus.ARCHIVED]: 'Arquivado',
};

export const LESSON_KIND_LABELS: Record<string, string> = {
  [LessonKind.VIDEO]: 'Vídeo',
  [LessonKind.TEXT]: 'Leitura',
  [LessonKind.QUIZ]: 'Quiz',
  [LessonKind.LIVE]: 'Ao vivo',
};

export function isPublished(course: Pick<CourseSummary, 'status'>): boolean {
  return course.status === CourseStatus.PUBLISHED;
}

/** "1 h 25 min", "12 min", "45 s". */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  if (s === 0) return '—';
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** "14:00", "1:05:30" — o formato de duração de vídeo. */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** As aulas na ordem do curso, módulo a módulo. */
export function lessonsInOrder(course: Pick<Course, 'modules'>): { lesson: Lesson; module: CourseModule }[] {
  return course.modules.flatMap((module) => module.lessons.map((lesson) => ({ lesson, module })));
}

export function lessonCountLabel(n: number): string {
  return n === 1 ? '1 aula' : `${n} aulas`;
}

/**
 * Por que o curso ainda não pode ser publicado.
 *
 * Mesma regra de `Course.publish()`: recusa curso vazio e aula de vídeo
 * sem transcodificação concluída (`available = false`).
 */
export function publishBlockers(course: Pick<Course, 'lesson_count' | 'modules'>): string[] {
  if (course.lesson_count === 0) return ['O curso precisa de pelo menos uma aula.'];
  return course.modules
    .flatMap((m) => m.lessons)
    .filter((l) => !l.available)
    .map((l) => `A aula "${l.title}" ainda não tem vídeo pronto.`);
}

/** Cor estável por curso, para a capa enquanto não há imagem. */
export function coverHue(courseId: string): number {
  let h = 0;
  for (const ch of courseId) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/** A ordem das aulas com uma delas movida uma posição (`-1` sobe, `1` desce). */
export function movedLesson(ids: readonly string[], id: string, delta: -1 | 1): string[] {
  const from = ids.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return [...ids];
  const next = [...ids];
  [next[from], next[to]] = [next[to]!, next[from]!];
  return next;
}

export interface CatalogStats {
  published: number;
  drafts: number;
  archived: number;
  lessons: number;
  totalDurationS: number;
}

export function catalogStats(list: readonly CourseSummary[]): CatalogStats {
  return {
    published: list.filter((c) => c.status === CourseStatus.PUBLISHED).length,
    drafts: list.filter((c) => c.status === CourseStatus.DRAFT).length,
    archived: list.filter((c) => c.status === CourseStatus.ARCHIVED).length,
    lessons: list.reduce((n, c) => n + c.lesson_count, 0),
    totalDurationS: list.reduce((n, c) => n + c.total_duration_s, 0),
  };
}

/** Sem acento e sem caixa: "nutrição" encontra "Nutricao". */
export const fold = (s: string) => s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();

export function matchesSearch(
  course: Pick<CourseSummary, 'title' | 'description' | 'tags' | 'slug'>,
  term: string,
) {
  if (!term.trim()) return true;
  return fold([course.title, course.slug, course.description, ...course.tags].join(' ')).includes(
    fold(term.trim()),
  );
}

// ── Edição otimista ────────────────────────────────────────────────
// O curso como ficará depois da edição: a tela muda na hora e, se a API
// recusar, o cache volta ao anterior.

/** O curso com os campos editados. */
export function withCourseChanges(
  course: Course,
  changes: Partial<Pick<Course, 'title' | 'description' | 'tags' | 'author_name'>>,
): Course {
  return { ...course, ...changes };
}

/** O curso com o módulo renomeado. */
export function withModuleTitle(course: Course, moduleId: string, title: string): Course {
  return { ...course, modules: course.modules.map((m) => (m.id === moduleId ? { ...m, title } : m)) };
}

/** O curso com a aula editada (título e/ou descrição). */
export function withLessonChanges(
  course: Course,
  lessonId: string,
  changes: Partial<Pick<Lesson, 'title' | 'description'>>,
): Course {
  return {
    ...course,
    modules: course.modules.map((m) =>
      m.lessons.some((l) => l.id === lessonId)
        ? { ...m, lessons: m.lessons.map((l) => (l.id === lessonId ? { ...l, ...changes } : l)) }
        : m,
    ),
  };
}
